import React from 'react';
import { Card } from '@/components/ui/cards/Card';
import { Text } from '@/components/ui/text/Text';
import { BarChart } from '@/components/ui/charts/BarChart';
import type { BarChartDataPoint } from '@/components/ui/charts/BarChart';
import { colors } from '@/assets/styles/colors';


interface BarChartCardProps {
  title: string;
  data: BarChartDataPoint[];
  formatTooltipValue?: (value: number) => string;
  // Optional slot rendered next to the title (e.g. an `ActionMenu` trigger for "Ver
  // detalle"), same purpose as `DonutSummaryCard`'s `headerAction`.
  headerAction?: React.ReactNode;
  'data-testid'?: string;
}

// Mirrors DonutSummaryCard.tsx's wrapping pattern (Card + uppercase title + headerAction
// slot) around a generic chart primitive — same separation of concerns as the donut
// cards: this component only lays out the Card/title/chart, all data and formatting
// come from the caller.
export const BarChartCard: React.FC<BarChartCardProps> = ({
  title,
  data,
  formatTooltipValue,
  headerAction,
  'data-testid': dataTestId,
}) => (
  <Card
    elevation={1}
    borderRadius="16px"
    padding="24px"
    style={{ display: 'flex', flexDirection: 'column', gap: 12, flex: 1, minWidth: 280 }}
    data-testid={dataTestId}
  >
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8 }}>
      <Text
        variant="body-medium"
        fontFamily="montserrat"
        color={colors.grays.neutral66}
        style={{ letterSpacing: '0.05em', textTransform: 'uppercase' }}
        data-testid={`${dataTestId}-title`}
      >
        {title}
      </Text>
      {headerAction}
    </div>

    <BarChart
      data={data}
      formatTooltipValue={formatTooltipValue}
      data-testid={`${dataTestId}-chart`}
    />
  </Card>
);

export default BarChartCard;
